import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { useEffect, useState } from 'react'
import { Wrapper } from './styles'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'src/state'
import Toggle from 'react-toggle'
import { OrderWidgetConfig, OrderWidgetButtonType, UserPatchType, BusinessLinkType } from '@meniudigital/shared'
import { patchCurrentUser } from 'src/state/users'
import IosToggle from '../../global/IosToggle'
import FormField from 'src/global/form/FormField'
import { IS_DESKTOP_OR_TABLET } from '../HomeScreen'
import OrderWidgetPreview from './OrderWidgetPreview'
import { Button, HighlightedInfo } from 'src/global'

const BUTTON_ORDER: OrderWidgetButtonType[] = ['website', 'phone', 'glovo', 'bolt', 'wolt']

export default function ConfigureOrderWidget() {
  const dispatch = useDispatch()
  const { user } = useSelector((state: State) => state.users)

  const restaurantName = user?.name || 'Restaurantul Tău'

  // Initialize state from user.orderWidget or empty config
  const [config, setConfig] = useState<OrderWidgetConfig>({
    isEnabled: user?.orderWidget?.isEnabled,
    discountPercent: user?.orderWidget?.discountPercent || 0,
    whenToDisplay: user?.orderWidget?.whenToDisplay || 'always',
    buttons: [],
  })

  // Initialize buttons ensuring all types exist
  useEffect(() => {
    const existingButtons = user?.orderWidget?.buttons || []
    const allButtons = BUTTON_ORDER.map(type => {
      const existing = existingButtons.find(b => b.type === type)
      return existing || { type, isEnabled: false, url: '' }
    })
    setConfig(prev => ({ ...prev, buttons: allButtons }))
  }, [user?.orderWidget])

  const updateDiscount = (value: string) => {
    const numValue = parseInt(value) || 0
    setConfig({ ...config, discountPercent: Math.min(Math.max(numValue, 0), 99) })
  }

  const updateWhenToDisplay = (mode: 'always' | 'separateUrl') => {
    setConfig({ ...config, whenToDisplay: mode })
  }

  const getButton = (type: OrderWidgetButtonType) => {
    return config.buttons.find(b => b.type === type)
  }

  const toggleButton = (type: OrderWidgetButtonType) => {
    const updatedButtons = config.buttons.map(b => (b.type === type ? { ...b, isEnabled: !b.isEnabled } : b))
    setConfig({ ...config, buttons: updatedButtons })
  }

  const updateButtonUrl = (type: OrderWidgetButtonType, url: string) => {
    const updatedButtons = config.buttons.map(b => (b.type === type ? { ...b, url: url.toLowerCase() } : b))
    setConfig({ ...config, buttons: updatedButtons })
  }

  const validateAndSave = async () => {
    const phoneRegex = /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/

    // Validate at least one button is enabled
    const hasActiveButton = config.buttons.some(btn => btn.isEnabled)
    if (!hasActiveButton) {
      window.showConfirm({ text: 'Trebuie să ai cel puțin un buton activ.' })
      return
    }

    // Validate URLs
    for (const button of config.buttons) {
      if (!button.isEnabled) {
        continue
      }

      if (button.type === 'phone') {
        if (!phoneRegex.test(button.url) || button.url.length > 15) {
          window.showConfirm({ text: 'Numărul de telefon nu este valid. Exemplu: +40756123456' })
          return
        }
      } else {
        if (!button.url.startsWith('https://') && !button.url.startsWith('http://')) {
          window.showConfirm({ text: 'URL-urile trebuie să înceapă cu http:// sau https://' })
          return
        }
      }
    }

    // Save to server
    dispatch(
      patchCurrentUser({
        type: UserPatchType.OrderWidgetConfig,
        data: config,
      })
    )

    await window.showConfirm({ text: 'Widget-ul a fost actualizat cu succes.' })
  }

  const getDisplayName = (type: OrderWidgetButtonType) => {
    const names: Record<OrderWidgetButtonType, string> = {
      website: 'Website',
      phone: 'Telefon',
      glovo: 'Glovo',
      bolt: 'Bolt Food',
      wolt: 'Wolt',
    }
    return names[type]
  }

  const getButtonIcon = (type: OrderWidgetButtonType) => {
    const icons: Record<OrderWidgetButtonType, string> = {
      website: '/icons/external-link.svg',
      phone: '/icons/phone-ios.png',
      glovo: '/icons/glovo.png',
      bolt: '/icons/bolt-food.png',
      wolt: '/icons/wolt.png',
    }
    return icons[type]
  }

  const getPlaceholder = (type: OrderWidgetButtonType) => {
    const placeholders: Record<OrderWidgetButtonType, string> = {
      website: 'https://www.restaurantul-tau.ro',
      phone: 'tel:+40756123456',
      glovo: 'https://glovoapp.com/ro/ro/cluj-napoca/restaurant-tău/',
      bolt: 'https://food.bolt.eu/ro-RO/297-cluj-napoca/p/12345-restaurant-tău',
      wolt: 'https://wolt.com/ro/rou/cluj-napoca/restaurant/restaurant-tău',
    }
    return placeholders[type]
  }

  const isWidgetVisible = user?.orderWidget?.isEnabled === true

  const copyUrlToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      await window.showConfirm({ text: 'URL-ul a fost copiat în clipboard!' })
    } catch (err) {
      await window.showConfirm({ text: 'Nu s-a putut copia URL-ul.' })
    }
  }

  const glovoButton = config.buttons.find(x => x.type === 'glovo')
  const phoneButton = config.buttons.find(x => x.type === 'phone')

  // Get the phone number from businessInfo
  const businessPhone = user?.businessInfo?.find(x => x.type === BusinessLinkType.PhoneNumber)?.url || ''

  // Normalize phone numbers for comparison (remove spaces, dashes, parentheses, 'tel:' prefix)
  const normalizePhone = (phone: string) => {
    return phone.replace(/^tel:/, '').replace(/[\s\-().]/g, '')
  }

  const isPhoneDifferent =
    phoneButton?.isEnabled &&
    phoneButton.url &&
    businessPhone &&
    normalizePhone(phoneButton.url) !== normalizePhone(businessPhone)

  return (
    <Wrapper className="page">
      <h3>Configurează widgetul de comenzi online</h3>

      {/* Preview Section */}
      <div className="preview-wrapper">
        <OrderWidgetPreview config={config} restaurantName={(restaurantName || '').trim()} showLabel={true} />
      </div>

      {/* Configuration Form */}
      <div className="config-form">
        <div className="form-section unified-section">
          {/* Buttons Section */}
          <h4 className="section-question">Ce butoane dorești să apară în widget?</h4>
          <div className="setting-explainer">
            Selectează butoanele care vor apărea în widget-ul de comenzi și completează URL-urile corespunzătoare pentru fiecare
            platformă.
          </div>
          <div className="button-cards-wrapper">
            {BUTTON_ORDER.map(buttonType => {
              const button = getButton(buttonType)
              if (!button) {
                return null
              }

              return (
                <div
                  key={buttonType}
                  className={`button-card ${button.isEnabled ? 'enabled expanded' : ''}`}
                  data-type={buttonType}
                >
                  <div className="button-card-header">
                    <div className="button-card-icon">
                      <img src={getButtonIcon(buttonType)} alt={getDisplayName(buttonType)} />
                    </div>
                    <span className="button-card-name">{getDisplayName(buttonType)}</span>
                    <div className="button-card-toggle">
                      <div className="react-toggle-wrapper">
                        <Toggle checked={button.isEnabled} icons={false} onChange={() => toggleButton(buttonType)} />
                      </div>
                    </div>
                  </div>
                  {button.isEnabled && (
                    <div className="button-card-content">
                      <input
                        value={button.url}
                        onChange={e => updateButtonUrl(buttonType, e.target.value)}
                        placeholder={getPlaceholder(buttonType)}
                        className="button-card-input"
                      />
                    </div>
                  )}
                </div>
              )
            })}
            {isPhoneDifferent && (
              <HighlightedInfo
                text={`Ai introdus un număr de telefon diferit (${phoneButton?.url}) față de cel din Setări (${businessPhone}). Asigură-te că ambele numere sunt corecte.`}
              />
            )}
            {glovoButton?.isEnabled && glovoButton?.url.includes('glovoapp.com') && (
              <HighlightedInfo text="Asigură-te că testezi linkurile pe telefon și funcționează cum te aștepți. Unele aplicații (precum Glovo) nu duc la pagina restaurantului dacă linkul e copiat din browser - funcționează doar dacă linkul e copiat de pe pagina restaurantului din aplicație." />
            )}
          </div>

          {/* Discount Section */}
          <h4 className="section-question">Oferi reducere pentru comenzi directe?</h4>
          <div className="setting-explainer">
            Dacă oferi o reducere clienților care comandă direct (prin site sau telefon), specifică procentul aici și va fi afișat
            în widget sub formă de etichetă.
          </div>
          <div className="discount-input-wrapper">
            <FormField
              product={{ discountPercent: config.discountPercent } as any}
              for="discountPercent"
              label=""
              asNumber
              max={99}
              placeholder="0"
              suffix="%"
              setProperty={value => updateDiscount(value)}
              style={{ marginBottom: 0 }}
            />
            <span className="discount-label">reducere la comenzi directe</span>
          </div>

          {/* Display Mode Section */}
          <h4 className="section-question">Când vrei să apară widget-ul?</h4>
          <div className="setting-explainer">
            Widget-ul poate să apară pe meniul tău <strong>întotdeauna</strong> (inclusiv la scanarea codului QR în restaurant)
            sau doar pe un <strong>URL separat</strong> dedicat comenzilor (meniudigital.ro/{user?.username}/comanda) pe care îl
            poți distribui pe Facebook, Instagram sau alte canale online.
            <br />
            <br />
            👉 Dacă restaurantul tău oferă doar comenzi online, îți recomandăm să alegi opțiunea "Întotdeauna". <br />
            👉 Dacă restaurantul tău oferă și servire la masă, poți alege opțiunea cu URL separat pentru a evita confuziile
            clienților care scanează codul QR în restaurant.
          </div>
          <IosToggle
            value={config.whenToDisplay}
            values={[
              { value: 'always', label: 'Întotdeauna' },
              { value: 'separateUrl', label: IS_DESKTOP_OR_TABLET ? 'Doar pe URL separat' : 'Doar URL separat' },
            ]}
            withIcon
            onChange={newValue => updateWhenToDisplay(newValue as 'always' | 'separateUrl')}
          />
          {config.whenToDisplay === 'always' ? (
            <div className="url-preview">
              Widget-ul va fi vizibil la orice vizită pe{' '}
              <span className="url">
                https://meniudigital.ro/{user?.username || 'demo'}
                <button
                  className="copy-url-button"
                  onClick={() => copyUrlToClipboard(`https://meniudigital.ro/${user?.username || 'demo'}`)}
                >
                  Copiază URL
                </button>
              </span>
            </div>
          ) : (
            <div className="url-preview">
              Widget-ul va fi vizibil doar pe URL-ul separat{' '}
              <span className="url">
                https://meniudigital.ro/{user?.username || 'demo'}/comanda
                <button
                  className="copy-url-button"
                  onClick={() => copyUrlToClipboard(`https://meniudigital.ro/${user?.username || 'demo'}/comanda`)}
                >
                  Copiază URL
                </button>
              </span>
            </div>
          )}
        </div>
      </div>

      {!isWidgetVisible && <HighlightedInfo text="Widgetul nu apare încă în meniu. Pentru a-l afișa, activează-l din Setări." />}

      <Button className="save-button" text="Salvează" onClick={validateAndSave} icon={faCheck} />
    </Wrapper>
  )
}
